/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.quoteNotification = this.querySelector('.js-added-to-quote');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('is-loading');

      if (this.notification) this.notification.hidden = true;
      if (this.quoteNotification) this.quoteNotification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        /* CUSTOM_SWATCHES_BY_CLAUDE — Lock body scroll on mobile when drawer opens */
        if (!window.theme.mediaMatches.md) {
          document.body.classList.add('overflow-hidden');
        }
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('is-loading');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      if (this._moqAbort) this._moqAbort.abort();
      if (this._moqObserver) this._moqObserver.disconnect();
      this.cleanupCalculator();
      this.content.innerHTML = '';

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        const html = await response.text();
        tmpl.innerHTML = html;

        // MODIFIED_BY_RYAN: Extract MOQ and step data from fetched product page
        this._currentProductId = null;
        const moqMatch = html.match(/window\.BSF_VariantMOQ\[(\d+)\]\s*=\s*(\{[^}]+\})/);
        if (moqMatch) {
          try {
            const productId = moqMatch[1];
            const moqData = JSON.parse(moqMatch[2].replace(/'/g, '"'));
            window.BSF_VariantMOQ = window.BSF_VariantMOQ || {};
            window.BSF_VariantMOQ[productId] = moqData;
            this._currentProductId = productId;
          } catch (e) {
            console.warn('MOQ extraction failed:', e);
          }
        }
        const stepMatch = html.match(/window\.BSF_VariantStep\[(\d+)\]\s*=\s*(\{[^}]+\})/);
        if (stepMatch) {
          try {
            const productId = stepMatch[1];
            const stepData = JSON.parse(stepMatch[2].replace(/'/g, '"'));
            window.BSF_VariantStep = window.BSF_VariantStep || {};
            window.BSF_VariantStep[productId] = stepData;
          } catch (e) {
            console.warn('Step extraction failed:', e);
          }
        }

        /* CUSTOM_ROLL_CALC_BY_CLAUDE — Extract roll dimensions for calculator */
        const rollDimsMatch = html.match(/window\.__rollDimensions\s*=\s*(\{[\s\S]*?\});/);
        if (rollDimsMatch) {
          try {
            const rollDims = new Function('return ' + rollDimsMatch[1])();
            window.__rollDimensions = window.__rollDimensions || {};
            Object.assign(window.__rollDimensions, rollDims);
          } catch (e) {
            console.warn('Roll dimensions extraction failed:', e);
          }
        }
        /* END CUSTOM_ROLL_CALC_BY_CLAUDE */

        this.fetchedTemplate = tmpl;
        this.productEl = tmpl.content.getElementById('quick-buy-template').content.querySelector('.js-product');
        this.renderProduct(opener);

        // Open drawer AFTER content is rendered so it slides up at final height (no snapping)
        /* CUSTOM_SWATCHES_BY_CLAUDE — Lock body scroll on mobile when drawer opens */
        if (!window.theme.mediaMatches.md) {
          document.body.classList.add('overflow-hidden');
        }
        super.open(opener);
      }

      this.fetch = null;
      opener.removeAttribute('aria-disabled');
      opener.classList.remove('is-loading');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      /* CUSTOM_SWATCHES_BY_CLAUDE — Unlock body scroll on mobile when drawer closes */
      if (!window.theme.mediaMatches.md) {
        document.body.classList.remove('overflow-hidden');
      }

      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

/**
 * Renders the product details.
 * @param {Element} opener - Element that triggered opening of the drawer.
 */
renderProduct(opener) {
  QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

  // Check whether to select the first variant.
  const variantPicker = this.productEl.querySelector('variant-picker');
  if (variantPicker) {
    this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
  }

  this.updateFooter();
  this.updateContent();

  // MODIFIED_BY_RYAN: Initialize MOQ for quick-add drawer
  this.initDrawerMOQ();

  /* CUSTOM_ROLL_CALC_BY_CLAUDE — Init calculator in quick buy */
  this.initCalculator();
  /* END CUSTOM_ROLL_CALC_BY_CLAUDE */

  if (opener.dataset.selectedColor && this.selectFirstVariant) {
    // Timeout to allow the VariantPicker to initialize
    setTimeout(this.setActiveVariant.bind(this, opener), 10);
  }
}

/**
 * MODIFIED_BY_RYAN: Initialize MOQ functionality for the quick-add drawer
 */
initDrawerMOQ() {
  // Clean up previous MOQ listeners and observer
  if (this._moqAbort) this._moqAbort.abort();
  if (this._moqObserver) this._moqObserver.disconnect();
  this._moqAbort = new AbortController();
  const signal = this._moqAbort.signal;

  const drawer = this;
  const productId = this._currentProductId;

  // Find the MOQ and step maps for THIS product
  let moqMap = null;
  let stepMap = null;
  if (productId && window.BSF_VariantMOQ && window.BSF_VariantMOQ[productId]) {
    moqMap = window.BSF_VariantMOQ[productId];
    stepMap = window.BSF_VariantStep && window.BSF_VariantStep[productId] || null;
  }

  if (!moqMap) return;

  // Read unit plural from rendered price in the drawer
  let moqUnitLabel = '';
  const priceEl = drawer.querySelector('.price[data-unit-plural]');
  if (priceEl && priceEl.dataset.unitPlural) {
    moqUnitLabel = priceEl.dataset.unitPlural;
  }

  let lastVariantId = null;
  let userIncreasedQty = false;

  function isManualQty(currentQty) {
    if (currentQty === 1) return false;
    const moqValues = Object.values(moqMap);
    for (let i = 0; i < moqValues.length; i++) {
      if (parseInt(moqValues[i], 10) === currentQty) return false;
    }
    return true;
  }

  function createLabel(qtyInput) {
    let label = qtyInput.querySelector('.moq-label');
    if (!label) {
      label = document.createElement('div');
      label.className = 'moq-label';
      label.style.position = 'absolute';
      label.style.top = 'calc(100% + 1px)';
      label.style.left = '0';
      label.style.fontSize = '11px';
      label.style.whiteSpace = 'nowrap';
      qtyInput.style.position = 'relative';
      qtyInput.appendChild(label);
    }
    return label;
  }

  function updateLabel(form, moq, currentQty) {
    const qtyInput = form.querySelector('quantity-input');
    if (!qtyInput) return;

    const shouldShow = moq > 1 && currentQty === moq;
    const label = createLabel(qtyInput);

    if (shouldShow) {
      label.textContent = 'MINIMUM QTY: ' + moq + (moqUnitLabel ? ' ' + moqUnitLabel : '');
    }

    requestAnimationFrame(() => {
      if (shouldShow) {
        label.classList.add('is-visible');
      } else {
        label.classList.remove('is-visible');
      }
    });
  }

  /* CUSTOM_MOQ_LEGEND_BY_CLAUDE — Show MOQ inline with color name in quick view legend */
  let legendStyleInjected = false;
  let lastLegendMoq = null;

  function injectLegendStyles() {
    if (legendStyleInjected) return;
    legendStyleInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      @media (min-width: 769px) {
        quick-add-drawer .option-selector legend.label {
          font-size: 16px;
        }
      }
      @media (max-width: 768px) {
        quick-add-drawer .option-selector legend.label {
          font-size: 15px;
        }
        quick-add-drawer .moq-legend-label {
          display: block;
          margin-left: 0;
          margin-top: 4px;
        }
        quick-add-drawer .moq-legend-label .moq-dash {
          display: none;
        }
      }
      quick-add-drawer .moq-label {
        display: none !important;
      }
      quick-add-drawer .moq-legend-label {
        display: inline;
        font-size: inherit;
        font-weight: 700;
        color: #006BCC;
        margin-left: 8px;
        opacity: 0;
        white-space: nowrap;
      }
      quick-add-drawer .moq-legend-label .moq-dash {
        color: #000F1C;
      }
      quick-add-drawer .moq-legend-label.is-visible {
        opacity: 1;
      }
      quick-add-drawer .moq-legend-label .moq-char {
        display: inline-block;
        animation: moqWave 0.4s ease-in-out forwards;
      }
    `;
    document.head.appendChild(style);
  }

  function updateLegendLabel(moq) {
    const legend = drawer.querySelector('.option-selector legend');
    if (!legend) return;

    const shouldShow = moq > 1;
    const moqChanged = moq !== lastLegendMoq;
    let label = legend.querySelector('.moq-legend-label');
    lastLegendMoq = moq;

    if (shouldShow) {
      injectLegendStyles();
      if (!label) {
        label = document.createElement('span');
        label.className = 'moq-legend-label';
        legend.appendChild(label);
      }
      const isVisible = label.classList.contains('is-visible');
      if (!isVisible || moqChanged) {
        const unit = moqUnitLabel;
        const text = 'MIN QTY: ' + moq + (unit ? ' ' + unit : '');
        let html = '<span class="moq-dash">-</span> ';
        for (let i = 0; i < text.length; i++) {
          const ch = text[i] === ' ' ? '&nbsp;' : text[i];
          html += '<span class="moq-char" style="animation-delay:' + (i * 40) + 'ms">' + ch + '</span>';
        }
        label.innerHTML = html;
        label.classList.remove('is-visible');
        void label.offsetHeight;
        label.classList.add('is-visible');
      }
    } else if (label) {
      label.classList.remove('is-visible');
    }
  }
  /* END CUSTOM_MOQ_LEGEND_BY_CLAUDE */

  function snapToStep(value, moq, step) {
    if (value <= moq) return moq;
    var remainder = (value - moq) % step;
    if (remainder === 0) return value;
    return value - remainder + step;
  }

  function applyMOQ(isVariantChange) {
    // Get fresh references every time since elements get replaced by variant-picker section rendering
    const form = document.getElementById('product-form-quickadd');
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    const qty = document.getElementById('quantity-quickadd');

    if (!idInput || !qty) return;

    const variantId = idInput.getAttribute('value') || idInput.value;
    if (!variantId) return;

    const currentMoq = moqMap[variantId] ? parseInt(moqMap[variantId], 10) : 1;
    const currentStep = stepMap && stepMap[variantId] ? parseInt(stepMap[variantId], 10) : 1;
    const currentQty = parseInt(qty.value, 10);
    const variantChanged = lastVariantId !== null && lastVariantId !== variantId;

    qty.min = String(currentMoq);
    qty.step = String(currentStep);

    if (variantChanged || isVariantChange) {
      const manuallySet = isManualQty(currentQty);

      if (currentQty < currentMoq) {
        qty.value = String(currentMoq);
        userIncreasedQty = false;
      } else if (manuallySet || userIncreasedQty) {
        qty.value = String(snapToStep(currentQty, currentMoq, currentStep));
      } else {
        qty.value = String(currentMoq);
        userIncreasedQty = false;
      }
    } else if (currentQty < currentMoq) {
      qty.value = String(currentMoq);
    } else {
      qty.value = String(snapToStep(currentQty, currentMoq, currentStep));
    }

    lastVariantId = variantId;

    const finalQty = parseInt(qty.value, 10);
    updateLabel(form, currentMoq, finalQty);
    updateLegendLabel(currentMoq);
  }

  // Listen for variant changes (fires AFTER variant-picker section rendering replaces content)
  drawer.addEventListener('on:variant:change', () => {
    setTimeout(() => applyMOQ(true), 50);
  }, { signal });

  // Track +/- button clicks and enforce step
  drawer.addEventListener('click', (evt) => {
    if (evt.target.closest('.qty-input__btn')) {
      setTimeout(() => {
        const qty = document.getElementById('quantity-quickadd');
        const form = document.getElementById('product-form-quickadd');
        const idInput = form?.querySelector('input[name="id"]');

        if (qty && idInput) {
          const variantId = idInput.getAttribute('value') || idInput.value;
          const currentMoq = moqMap[variantId] ? parseInt(moqMap[variantId], 10) : 1;
          const currentStep = stepMap && stepMap[variantId] ? parseInt(stepMap[variantId], 10) : 1;
          let currentQty = parseInt(qty.value, 10);

          if (currentQty > 0 && currentQty < currentMoq) {
            currentQty = currentMoq;
          } else if (currentQty > currentMoq) {
            currentQty = snapToStep(currentQty, currentMoq, currentStep);
          }
          qty.value = String(currentQty);
          userIncreasedQty = currentQty > currentMoq;
          updateLabel(form, currentMoq, currentQty);
        }
      }, 50);
    }
  }, { signal });

  // MutationObserver: re-apply MOQ whenever the footer content is replaced
  // (variant-picker section rendering replaces the add-to-cart div, resetting qty to 1)
  this._moqObserver = new MutationObserver(() => {
    const qty = document.getElementById('quantity-quickadd');
    if (qty && parseInt(qty.min, 10) <= 1) {
      applyMOQ(false);
    }
  });
  if (this.footerContent) {
    this._moqObserver.observe(this.footerContent, { childList: true, subtree: true });
  }

  // Apply immediately (no delay — DOM is ready since renderProduct already set innerHTML)
  applyMOQ(false);
}

/* CUSTOM_ROLL_CALC_BY_CLAUDE — Calculator integration for quick buy drawer */

/**
 * Initialize the Quantity Calculator inside the quick buy drawer.
 * Extracts calculator HTML/CSS/JS from the fetched product page template.
 */
initCalculator() {
  if (!this.fetchedTemplate) return;

  // a) Find the calculator overlay in the fetched page
  const overlay = this.fetchedTemplate.content.querySelector('[id^="ai-calc-overlay-"]');
  if (!overlay) return; // Product has no calculator

  // b) Find the calculator trigger button (always a <button>, other bar btns are <a> tags)
  const buttonBar = this.fetchedTemplate.content.querySelector('[class*="ai-calc-button-bar-"]');
  const triggerBtn = buttonBar ? buttonBar.querySelector('button[type="button"]') : null;
  if (!triggerBtn) return;

  // c) Extract the aiGenId from the overlay ID
  const aiGenId = overlay.id.replace('ai-calc-overlay-', '');

  // d) Extract calculator CSS — find <style> containing calculator classes
  let calcStyleText = '';
  this.fetchedTemplate.content.querySelectorAll('style').forEach(function(s) {
    if (s.textContent.indexOf('ai-calc-overlay-' + aiGenId) !== -1) {
      calcStyleText += s.textContent;
    }
  });

  // e) Extract calculator JS — find <script> containing the web component definition
  let calcScriptEl = null;
  this.fetchedTemplate.content.querySelectorAll('script').forEach(function(s) {
    if (s.textContent.indexOf('ProductCalculator' + aiGenId) !== -1) {
      calcScriptEl = s;
    }
  });

  // f) Inject CSS into <head>
  if (calcStyleText) {
    const styleEl = document.createElement('style');
    styleEl.textContent = calcStyleText;
    styleEl.dataset.quickAddCalc = aiGenId;
    document.head.appendChild(styleEl);
    this._calcStyle = styleEl;
  }

  // g) Inject the calculator trigger button into the drawer content
  const btnWrapper = document.createElement('div');
  btnWrapper.className = 'quick-add__calculator-btn';
  const clonedBtn = triggerBtn.cloneNode(true);
  // Rewire onclick to target the overlay in its new location on document.body
  clonedBtn.setAttribute('onclick',
    "var o=document.getElementById('ai-calc-overlay-" + aiGenId + "');" +
    "if(o){o.classList.add('active');document.body.style.overflow='hidden';}" +
    "if(navigator.vibrate)navigator.vibrate(50);"
  );
  btnWrapper.appendChild(clonedBtn);
  this.footerContent.insertBefore(btnWrapper, this.footerContent.firstChild);

  // h) Inject overlay into document.body
  const overlayClone = overlay.cloneNode(true);
  // Rewire the overlay backdrop click to close properly
  overlayClone.setAttribute('onclick',
    "if(event.target===this){" +
      "var c=this.querySelector('[data-calculation-mode]');" +
      "if(c&&c.handlePopupClose)c.handlePopupClose();" +
      "this.classList.remove('active');document.body.style.overflow='';" +
    "}"
  );
  // Rewire close button
  const closeBtn = overlayClone.querySelector('[class*="ai-calc-popup-close-"]');
  if (closeBtn) {
    closeBtn.setAttribute('onclick',
      "var o=document.getElementById('ai-calc-overlay-" + aiGenId + "');" +
      "var c=o.querySelector('[data-calculation-mode]');" +
      "if(c&&c.handlePopupClose)c.handlePopupClose();" +
      "o.classList.remove('active');document.body.style.overflow='';" +
      "if(navigator.vibrate)navigator.vibrate(50);"
    );
  }
  this._calcOverlay = overlayClone;
  this._calcGenId = aiGenId;

  // i) Execute calculator JS if custom element not yet defined
  //    Must happen BEFORE appending overlay so connectedCallback can initialize
  const tagName = 'product-calculator-' + aiGenId;
  if (calcScriptEl && !customElements.get(tagName)) {
    const newScript = document.createElement('script');
    newScript.textContent = calcScriptEl.textContent;
    newScript.dataset.quickAddCalc = aiGenId;
    document.body.appendChild(newScript);
  }

  // Now append overlay to body — connectedCallback fires and initializes the component
  document.body.appendChild(overlayClone);

  // j) Patch handlePopupClose to target Quick Buy form instead of main product form
  var self = this;
  setTimeout(function() {
    if (!self._calcOverlay) return;
    var calcComponent = self._calcOverlay.querySelector('[data-calculation-mode]');
    if (!calcComponent) return;

    calcComponent.handlePopupClose = function() {
      if (!this._primaryResult || this._primaryResult <= 0) return;

      var productForm = document.getElementById('product-form-quickadd');
      if (!productForm) return;

      var qtyInput = productForm.querySelector('input[name="quantity"]');
      if (qtyInput) {
        var nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
        nativeSetter.call(qtyInput, this._primaryResult);
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Inject hidden line item properties into Quick Buy form
      if (this._rollDetails) {
        var props = {
          'Room Dimensions': this._rollDetails.dimensions,
          'Roll Layout': this._rollDetails.layout
        };
        var keys = Object.keys(props);
        for (var i = 0; i < keys.length; i++) {
          var inputName = 'properties[' + keys[i] + ']';
          var existing = productForm.querySelector('input[name="' + inputName + '"]');
          if (existing) {
            existing.value = props[keys[i]];
          } else {
            var hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = inputName;
            hidden.value = props[keys[i]];
            hidden.className = 'js-roll-calc-property';
            productForm.appendChild(hidden);
          }
        }
      }
    };
  }, 500);
}

/**
 * Remove calculator artifacts from a previous product.
 */
cleanupCalculator() {
  if (this._calcOverlay && this._calcOverlay.parentNode) {
    this._calcOverlay.parentNode.removeChild(this._calcOverlay);
  }
  this._calcOverlay = null;

  if (this._calcStyle && this._calcStyle.parentNode) {
    this._calcStyle.parentNode.removeChild(this._calcStyle);
  }
  this._calcStyle = null;

  var calcBtn = this.footerContent ? this.footerContent.querySelector('.quick-add__calculator-btn') : null;
  if (calcBtn) calcBtn.remove();

  this._calcGenId = null;
  this.fetchedTemplate = null;
}

/* END CUSTOM_ROLL_CALC_BY_CLAUDE */








    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /* CUSTOM_RFQ_BY_CLAUDE — "Added to quote" notification in quick buy drawer */
    /**
     * Shows an "Added to quote" message in the drawer.
     */
    addedToQuote() {
      if (this.quoteNotification) {
        setTimeout(() => {
          this.quoteNotification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.quoteNotification.hidden = true;
        }, this.quoteNotification.dataset.visibleFor);
      }
    }
    /* END CUSTOM_RFQ_BY_CLAUDE */

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}